import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  Plus, List, MessageSquare, LinkIcon, Trash2, Edit, Download,
  AlertCircle, CheckCircle, X, Copy
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const SurveyPage = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  
  const [activeTab, setActiveTab] = useState('new');
  const [loading, setLoading] = useState(false);
  
  // Survey creation states
  const [surveyTitle, setSurveyTitle] = useState('');
  const [surveyDescription, setSurveyDescription] = useState('');
  const [questions, setQuestions] = useState([]);
  
  // Lists
  const [surveys, setSurveys] = useState([]);
  const [publicLinks, setPublicLinks] = useState([]);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [responses, setResponses] = useState([]);

  const questionTypes = [
    { value: 'multiple_choice', label: 'Múltipla Escolha (uma opção)' },
    { value: 'checkbox', label: 'Múltipla Seleção (várias opções)' },
    { value: 'short_text', label: 'Texto Curto' },
    { value: 'long_text', label: 'Texto Longo' },
    { value: 'likert', label: 'Escala Likert (1-5)' },
    { value: 'yes_no', label: 'Sim/Não' }
  ];

  useEffect(() => {
    if (activeTab === 'list') {
      loadSurveys();
    } else if (activeTab === 'public-links') {
      loadPublicLinks();
    } else if (activeTab === 'responses') {
      loadSurveys(); // Carregar pesquisas para seleção
      if (selectedSurvey) {
        loadSurveyResponses(selectedSurvey.id);
      }
    }
  }, [activeTab, selectedSurvey]);

  const loadSurveys = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendUrl}/api/surveys`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSurveys(response.data);
    } catch (error) {
      console.error('Erro ao carregar pesquisas:', error);
    }
  };

  const loadPublicLinks = async () => {
    try {
      const token = localStorage.getItem('token');
      const [linksResponse, surveysResponse] = await Promise.all([
        axios.get(`${backendUrl}/api/surveys/public-links-list`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${backendUrl}/api/surveys`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      // Mapear links com informações das pesquisas
      const linksWithSurveys = linksResponse.data.map(link => {
        const survey = surveysResponse.data.find(s => s.id === link.survey_id);
        return {
          ...link,
          survey_title: survey?.title || 'Pesquisa não encontrada'
        };
      });
      
      setPublicLinks(linksWithSurveys);
    } catch (error) {
      console.error('Erro ao carregar links públicos:', error);
    }
  };

  const loadSurveyResponses = async (surveyId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendUrl}/api/surveys/${surveyId}/responses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResponses(response.data);
    } catch (error) {
      console.error('Erro ao carregar respostas:', error);
    }
  };

  const addQuestion = () => {
    const newQuestion = {
      id: `q_${Date.now()}`,
      question_text: '',
      question_type: 'multiple_choice',
      options: ['', ''],
      required: false,
      order: questions.length
    };
    setQuestions([...questions, newQuestion]);
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index][field] = value;
    setQuestions(updated);
  };

  const addOption = (questionIndex) => {
    const updated = [...questions];
    updated[questionIndex].options.push('');
    setQuestions(updated);
  };

  const updateOption = (questionIndex, optionIndex, value) => {
    const updated = [...questions];
    updated[questionIndex].options[optionIndex] = value;
    setQuestions(updated);
  };

  const removeOption = (questionIndex, optionIndex) => {
    const updated = [...questions];
    updated[questionIndex].options.splice(optionIndex, 1);
    setQuestions(updated);
  };

  const removeQuestion = (index) => {
    const updated = questions.filter((_, i) => i !== index);
    setQuestions(updated);
  };

  const handleCreateSurvey = async () => {
    if (!surveyTitle.trim()) {
      toast({
        title: 'Atenção',
        description: 'Por favor, informe o título da pesquisa',
        variant: 'destructive'
      });
      return;
    }

    if (questions.length === 0) {
      toast({
        title: 'Atenção',
        description: 'Adicione pelo menos uma pergunta',
        variant: 'destructive'
      });
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const payload = {
        title: surveyTitle,
        description: surveyDescription,
        questions: questions
      };

      await axios.post(`${backendUrl}/api/surveys`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: 'Sucesso!',
        description: 'Pesquisa criada com sucesso',
      });

      // Reset form
      setSurveyTitle('');
      setSurveyDescription('');
      setQuestions([]);
      setActiveTab('list');

    } catch (error) {
      console.error('Erro ao criar pesquisa:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível criar a pesquisa',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePublicLink = async (surveyId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${backendUrl}/api/surveys/public-link`,
        { survey_id: surveyId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const publicUrl = `${window.location.origin}/survey/public/${response.data.token}`;
      
      toast({
        title: 'Link Criado!',
        description: 'Link público criado com sucesso',
      });

      // Recarregar links e mudar para a aba de Links Públicos
      await loadPublicLinks();
      setActiveTab('public-links');
      
    } catch (error) {
      console.error('Erro ao criar link público:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível criar o link público',
        variant: 'destructive'
      });
    }
  };

  const copyToClipboard = (token) => {
    const url = `${window.location.origin}/survey/public/${token}`;
    navigator.clipboard.writeText(url);
    toast({
      title: 'Copiado!',
      description: 'Link copiado para a área de transferência',
    });
  };

  const handleExportExcel = async (surveyId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${backendUrl}/api/surveys/${surveyId}/export-excel`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `pesquisa_respostas_${surveyId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast({
        title: 'Sucesso!',
        description: 'Excel exportado com sucesso',
      });
    } catch (error) {
      console.error('Erro ao exportar Excel:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível exportar para Excel',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
          <MessageSquare className="h-8 w-8 text-emerald-600" />
          Pesquisas Personalizadas
        </h1>
        <p className="text-gray-600 mt-2">
          Crie pesquisas customizadas para coleta de feedback e informações
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="new">
            <Plus className="h-4 w-4 mr-2" />
            Nova Pesquisa
          </TabsTrigger>
          <TabsTrigger value="list">
            <List className="h-4 w-4 mr-2" />
            Minhas Pesquisas
          </TabsTrigger>
          <TabsTrigger value="responses">
            <MessageSquare className="h-4 w-4 mr-2" />
            Respostas
          </TabsTrigger>
          <TabsTrigger value="public-links">
            <LinkIcon className="h-4 w-4 mr-2" />
            Links Públicos
          </TabsTrigger>
        </TabsList>

        {/* Nova Pesquisa */}
        <TabsContent value="new" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Criar Nova Pesquisa</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Título da Pesquisa *</label>
                <input
                  type="text"
                  value={surveyTitle}
                  onChange={(e) => setSurveyTitle(e.target.value)}
                  className="w-full p-2 border rounded"
                  placeholder="Ex: Pesquisa de Satisfação 2025"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Descrição (opcional)</label>
                <textarea
                  value={surveyDescription}
                  onChange={(e) => setSurveyDescription(e.target.value)}
                  className="w-full p-2 border rounded"
                  rows={3}
                  placeholder="Descreva o objetivo desta pesquisa"
                />
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">Perguntas</h3>
                  <Button onClick={addQuestion} size="sm">
                    <Plus className="h-4 w-4 mr-2" />
                    Adicionar Pergunta
                  </Button>
                </div>

                {questions.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>Nenhuma pergunta adicionada ainda</p>
                  </div>
                )}

                {questions.map((q, qIndex) => (
                  <Card key={q.id} className="mb-4 border-l-4 border-l-emerald-500">
                    <CardContent className="pt-6 space-y-4">
                      <div className="flex justify-between items-start gap-4">
                        <div className="flex-1 space-y-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium mb-2">
                                Pergunta {qIndex + 1} *
                              </label>
                              <input
                                type="text"
                                value={q.question_text}
                                onChange={(e) => updateQuestion(qIndex, 'question_text', e.target.value)}
                                className="w-full p-2 border rounded"
                                placeholder="Digite sua pergunta"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium mb-2">
                                Tipo de Resposta
                              </label>
                              <select
                                value={q.question_type}
                                onChange={(e) => updateQuestion(qIndex, 'question_type', e.target.value)}
                                className="w-full p-2 border rounded"
                              >
                                {questionTypes.map(type => (
                                  <option key={type.value} value={type.value}>
                                    {type.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>

                          {(q.question_type === 'multiple_choice' || q.question_type === 'checkbox') && (
                            <div className="border-t pt-4">
                              <div className="flex justify-between items-center mb-2">
                                <label className="text-sm font-medium">Opções de Resposta</label>
                                <Button
                                  onClick={() => addOption(qIndex)}
                                  size="sm"
                                  variant="outline"
                                >
                                  <Plus className="h-3 w-3 mr-1" />
                                  Opção
                                </Button>
                              </div>
                              {q.options && q.options.map((opt, optIndex) => (
                                <div key={optIndex} className="flex gap-2 mb-2">
                                  <input
                                    type="text"
                                    value={opt}
                                    onChange={(e) => updateOption(qIndex, optIndex, e.target.value)}
                                    className="flex-1 p-2 border rounded"
                                    placeholder={`Opção ${optIndex + 1}`}
                                  />
                                  <Button
                                    onClick={() => removeOption(qIndex, optIndex)}
                                    size="sm"
                                    variant="ghost"
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}

                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              id={`required_${qIndex}`}
                              checked={q.required}
                              onChange={(e) => updateQuestion(qIndex, 'required', e.target.checked)}
                              className="rounded"
                            />
                            <label htmlFor={`required_${qIndex}`} className="text-sm">
                              Pergunta obrigatória
                            </label>
                          </div>
                        </div>

                        <Button
                          onClick={() => removeQuestion(qIndex)}
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Button
                onClick={handleCreateSurvey}
                disabled={loading}
                className="w-full bg-emerald-600 hover:bg-emerald-700"
                size="lg"
              >
                {loading ? 'Criando...' : 'Criar Pesquisa'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Minhas Pesquisas */}
        <TabsContent value="list" className="space-y-4">
          {surveys.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <List className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                <p className="text-gray-600">Nenhuma pesquisa criada ainda</p>
              </CardContent>
            </Card>
          ) : (
            surveys.map(survey => (
              <Card key={survey.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold mb-2">{survey.title}</h3>
                      {survey.description && (
                        <p className="text-gray-600 mb-3">{survey.description}</p>
                      )}
                      <div className="flex gap-4 text-sm text-gray-500">
                        <span>📋 {survey.questions?.length || 0} perguntas</span>
                        <span>📅 {new Date(survey.created_at).toLocaleDateString('pt-BR')}</span>
                        <span className={`px-2 py-1 rounded ${
                          survey.status === 'active' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          {survey.status === 'active' ? 'Ativa' : 'Fechada'}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleCreatePublicLink(survey.id)}
                        size="sm"
                        variant="outline"
                      >
                        <LinkIcon className="h-4 w-4 mr-2" />
                        Criar Link
                      </Button>
                      <Button
                        onClick={() => {
                          setSelectedSurvey(survey);
                          loadSurveyResponses(survey.id);
                          setActiveTab('responses');
                        }}
                        size="sm"
                      >
                        Ver Respostas
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        {/* Respostas */}
        <TabsContent value="responses" className="space-y-4">
          {selectedSurvey ? (
            <>
              <div className="flex items-center gap-2 mb-4">
                <Button
                  onClick={() => setSelectedSurvey(null)}
                  variant="outline"
                  size="sm"
                >
                  ← Voltar
                </Button>
              </div>
              
              <Card>
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>{selectedSurvey.title}</CardTitle>
                    <Button
                      onClick={() => handleExportExcel(selectedSurvey.id)}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Exportar Excel
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 mb-4">
                    Total de respostas: <strong>{responses.length}</strong>
                  </p>
                  
                  {responses.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>Nenhuma resposta recebida ainda</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {responses.map((response, responseIndex) => (
                        <Card key={response.id} className="border-l-4 border-l-emerald-500">
                          <CardContent className="pt-6">
                            {/* Cabeçalho da Resposta */}
                            <div className="flex justify-between items-start mb-4 pb-4 border-b">
                              <div>
                                <p className="font-bold text-lg">Resposta #{responseIndex + 1}</p>
                                <p className="font-semibold text-emerald-600">{response.respondent_name || 'Anônimo'}</p>
                                <p className="text-sm text-gray-500">{response.respondent_email || 'Sem email'}</p>
                              </div>
                              <span className="text-sm text-gray-500">
                                {new Date(response.submitted_at).toLocaleString('pt-BR')}
                              </span>
                            </div>

                            {/* Respostas Detalhadas */}
                            <div className="space-y-4">
                              {selectedSurvey.questions.map((question, qIndex) => {
                                const answer = response.answers.find(a => a.question_id === question.id);
                                
                                let answerDisplay = 'Não respondido';
                                
                                if (answer) {
                                  if (answer.answer) {
                                    answerDisplay = answer.answer;
                                  } else if (answer.selected_options && answer.selected_options.length > 0) {
                                    answerDisplay = answer.selected_options.join(', ');
                                  } else if (answer.likert_score !== null && answer.likert_score !== undefined) {
                                    answerDisplay = `${answer.likert_score}/5`;
                                  }
                                }

                                return (
                                  <div key={question.id} className="bg-gray-50 p-4 rounded">
                                    <p className="font-semibold text-gray-700 mb-2">
                                      {qIndex + 1}. {question.question_text}
                                    </p>
                                    <p className="text-gray-900 pl-4">
                                      {answerDisplay}
                                    </p>
                                  </div>
                                );
                              })}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              <Card>
                <CardHeader>
                  <CardTitle>Selecione uma Pesquisa</CardTitle>
                </CardHeader>
                <CardContent>
                  {surveys.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>Nenhuma pesquisa criada ainda</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {surveys.map(survey => (
                        <Card 
                          key={survey.id} 
                          className="hover:shadow-md transition-shadow cursor-pointer"
                          onClick={async () => {
                            setSelectedSurvey(survey);
                            await loadSurveyResponses(survey.id);
                          }}
                        >
                          <CardContent className="pt-4">
                            <div className="flex justify-between items-center">
                              <div>
                                <h3 className="font-semibold">{survey.title}</h3>
                                <p className="text-sm text-gray-500">
                                  Criada em: {new Date(survey.created_at).toLocaleDateString('pt-BR')}
                                </p>
                              </div>
                              <Button
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedSurvey(survey);
                                  loadSurveyResponses(survey.id);
                                }}
                              >
                                Ver Respostas
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </TabsContent>

        {/* Links Públicos */}
        <TabsContent value="public-links" className="space-y-4">
          {publicLinks.length === 0 ? (
            <Card>
              <CardContent className="text-center py-12">
                <LinkIcon className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                <p className="text-gray-600">Nenhum link público criado ainda</p>
              </CardContent>
            </Card>
          ) : (
            publicLinks.map(link => (
              <Card key={link.id}>
                <CardContent className="pt-6">
                  <div className="space-y-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold text-lg">{link.survey_title}</h3>
                        <p className="text-sm text-gray-500">
                          Criado em: {new Date(link.created_at).toLocaleString('pt-BR')}
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${
                        link.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {link.active ? 'Ativo' : 'Inativo'}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="bg-gray-100 px-3 py-2 rounded text-sm flex-1 overflow-x-auto">
                        {`${window.location.origin}/survey/public/${link.token}`}
                      </code>
                      <Button
                        onClick={() => copyToClipboard(link.token)}
                        size="sm"
                        variant="outline"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SurveyPage;
